import { Box, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import OperationIcon from "src/assets/svg-icons/side-bar/operation.svg";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import { useGetSalarySalary } from "src/api/salary/salaries";
import FileItem from "src/shared/components/file-item";
import { SalaryDetail } from "src/types/salary";
import { downloadFileFromUrl, formatBytes, formatNumber } from "src/utils";
import { statusLabelKey } from "../table";
import { salaryStatusColor } from "../constants";
import SalaryEventsTable from "./events-table";
import InfoItem from "./info-item";
import SalaryOwnersPaymentsTable from "./owners-payments-table/index";
import SalaryPaymentsTable from "./payments-table";

const SalaryDetailsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation("salary");
  const { t: generalT } = useTranslation("general");

  const salaryId = id ? Number(id) : null;
  const { data, isLoading, refetch } = useGetSalarySalary(salaryId);
  const detail: SalaryDetail | undefined = data?.data;

  const goToList = () => navigate("/salary/salaries");

  const paidTotal = detail
    ? Number(detail.paid_total ?? 0)
    : 0;

  return (
    <Stack gap={3} p={{ xs: 2, sm: 4 }}>
      <Stack
        direction={"row"}
        spacing={"12px"}
        alignItems={"center"}
        color={"#101828"}
      >
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"60px"}
          height={"60px"}
          bgcolor={"#D0D5DD"}
          borderRadius={"16px"}
        >
          <img src={OperationIcon} width={"32px"} alt="" />
        </Box>
        <Typography fontSize={"32px"} lineHeight={"38.4px"}>
          {t("salaryDetails")}
        </Typography>
      </Stack>
      {/* divider */}
      <Box bgcolor={"#F2F4F7"} width={"100%"} height={"1px"} />

      <Stack spacing={2} direction={"row"} alignItems={"center"}>
        <Typography
          color={"#475467"}
          lineHeight={"19.2px"}
          onClick={goToList}
          sx={{ cursor: "pointer" }}
        >
          {t("payroll")}
        </Typography>
        <img src={RightArrow} alt="" />
        <Typography
          color={"#475467"}
          lineHeight={"19.2px"}
          onClick={goToList}
          sx={{ cursor: "pointer" }}
        >
          {t("salaries")}
        </Typography>
        <img src={RightArrow} alt="" />
        <Box borderRadius={"4px"} bgcolor={"#EAECF0"} p={"4px 8px"}>
          <Typography color={"#344054"} fontWeight={600} lineHeight={"19.2px"}>
            {detail?.reference}
          </Typography>
        </Box>
      </Stack>

      {isLoading || !detail ? (
        <Box display={"flex"} justifyContent={"center"} pt={20}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Stack bgcolor={"#F9FAFB"} spacing={1.5} p={2}>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography
                fontSize={"14px"}
                color={"#344054"}
                fontWeight={700}
                lineHeight={"18.8px"}
              >
                {generalT("branch")}:
                <Typography
                  component={"span"}
                  fontWeight={400}
                  fontSize={"14px"}
                  lineHeight={"18.8px"}
                >
                  {" "}
                  {detail.branch?.name}
                </Typography>
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                color={salaryStatusColor(detail.status)}
                label={t(statusLabelKey(detail.status))}
                sx={{ height: 22 }}
              />
            </Stack>

            <InfoItem
              title={t("employee")}
              text={detail.employee?.name ?? ""}
              label={t("salaryMonth")}
              value={detail.salary_month ?? "-"}
            />
            <InfoItem
              title={t("basicSalary")}
              text={formatNumber(detail.basic_salary)}
              label={t("allowanceTotal")}
              value={formatNumber(detail.allowance_total)}
            />
            <InfoItem
              title={t("additionalPay")}
              text={formatNumber(detail.additional_pay)}
              label={t("deduction")}
              value={formatNumber(detail.deduction)}
            />
            <InfoItem
              title={t("netPaySalary")}
              text={formatNumber(detail.net_pay_salary)}
              label={generalT("paid")}
              value={formatNumber(paidTotal)}
            />
            <InfoItem
              title={generalT("remainingAmount")}
              text={formatNumber(detail.remaining_amount)}
              label={t("paymentDate")}
              value={detail.payment_date ?? "-"}
            />
            <InfoItem
              title={generalT("receivedDate")}
              text={detail.received_date ?? "-"}
              label={generalT("notes")}
              value={detail.note ?? "-"}
            />

            <Stack gap={3} direction={"row"} maxWidth={"50%"} flexWrap={"wrap"}>
              {detail.files?.map((attachment) =>
                attachment.file ? (
                  <FileItem
                    isDeletable={false}
                    key={attachment.id}
                    name={`${attachment.file.name} .${attachment.file.extension}`}
                    info={formatBytes(attachment.file.size ?? 0)}
                    isDownloadable
                    onDownload={() => {
                      downloadFileFromUrl(
                        attachment.file?.url ?? "",
                        attachment.file?.name ?? "",
                      );
                    }}
                  />
                ) : null,
              )}
            </Stack>
          </Stack>

          <SalaryPaymentsTable
            list={detail.payments ?? []}
            isLoading={isLoading}
            refetch={refetch}
          />
          <SalaryEventsTable
            list={detail.event_deductions ?? []}
            isLoading={isLoading}
            refetch={refetch}
          />
          <SalaryOwnersPaymentsTable
            list={detail.salary_capitals ?? []}
            isLoading={isLoading}
            refetch={refetch}
          />
        </>
      )}
    </Stack>
  );
};

export default SalaryDetailsPage;
